import { KEEP_PLACE } from "@/lib/search";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { FAX_SORT_KEYS, type FaxSortKey, errorWords } from "@alm/domain";
import { EmptyState } from "@/components/app/lists/empty-state";
import { FileRecipientDialog } from "@/components/app/sending/file-recipient-dialog";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { SendDocumentsEmailDialog } from "@/components/app/sending/send-documents-email-dialog";
import { SendFaxDialog } from "@/components/app/sending/send-fax-dialog";
import { StatTile } from "@/components/app/lists/stat-tile";
import { StatTiles } from "@/components/app/lists/stat-tiles";
import { useRecipientBook } from "@/components/app/sending/use-recipient-book";
import { Skeleton } from "@/components/ui/skeleton";
import { useFacility } from "@/lib/facility/facility";
import { cn } from "@/lib/utils";
import { orpc, orpcClient } from "@/lib/orpc";
import { invalidateKeys } from "@/lib/query/invalidate-keys";
import { useCan, useCanWithoutBuilding, useFeature } from "@/lib/policy";
import { AssignFacilityDialog } from "./-components/assign-facility-dialog";
import { FaxDetailDialog } from "./-components/fax-detail-dialog";
import { faxPageSize } from "./-components/fax-page-size";
import { FaxFilters, type FaxFilterValues } from "./-components/fax-filters";
import { FileOnChartDialog } from "./-components/file-on-chart-dialog";
import { FaxHeaderActions } from "./-components/fax-header-actions";
import { FaxLogTable } from "./-components/fax-log-table";
import { openLink } from "@/lib/links/in-app";
import { keepWhileSame } from "@/lib/query/keep-while-same";
import { faxReaders } from "@/lib/query/query-readers";

const text = (value: unknown): string | undefined =>
  typeof value === "string" && value ? value : undefined;

const sortKey = (value: unknown): FaxSortKey =>
  FAX_SORT_KEYS.find((key) => key === value) ?? "date";

export const Route = createFileRoute("/_authenticated/_communications/fax/")({
  validateSearch: (search: Record<string, unknown>) => ({
    unfiled: search.unfiled === true || search.unfiled === "true" ? true : undefined,
    q: text(search.q),
    direction:
      search.direction === "inbound" || search.direction === "outbound"
        ? (search.direction as "inbound" | "outbound")
        : undefined,
    status: text(search.status),
    page: Number(search.page) > 1 ? Number(search.page) : undefined,
    pageSize: faxPageSize(search.pageSize),
    faxNumberId: text(search.faxNumberId),
    dateFrom: text(search.dateFrom),
    dateTo: text(search.dateTo),
    sortBy: sortKey(search.sortBy),
    sortDir: search.sortDir === "asc" ? ("asc" as const) : ("desc" as const),
  }),
  component: FaxInbox,
});

const fail = (error: Error) => toast.error(errorWords(error));

/** What the fax number received, and what still needs filing. */
function FaxInbox() {
  const { facilityId } = useFacility();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const canManage = useCan("fax:manage");
  /* Composing is floor work: the staff baseline carries `fax:send` and not
     `fax:manage`, and gating the composer on the latter took the Send button
     away from everybody who actually faxes a physician's office. */
  const canSend = useCanWithoutBuilding("fax:send");
  const canFileNetwork = useCan("directory:manage");
  const directoryEnabled = useFeature("directory");
  const queryClient = useQueryClient();

  const [composing, setComposing] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [assigning, setAssigning] = useState<{ id: string; facilityId: string | null } | null>(
    null,
  );
  const [filing, setFiling] = useState<string | null>(null);
  const [showing, setShowing] = useState<string | null>(null);
  const [filingNumber, setFilingNumber] = useState<string | null>(null);

  /* Keyed on numbers so a row never offers to file one already filed. Matched on digits, because
   * messages carry E.164 and records carry whatever was typed. */
  const page = search.page ?? 1;
  const pageSize = search.pageSize ?? 25;
  const canFile = canSend || (canFileNetwork && directoryEnabled);
  const book = useRecipientBook("fax", facilityId, canFile);

  const overview = useQuery(
    orpc.communications.fax.overview.queryOptions({ input: { facilityId } }),
  );
  const numbers = useQuery(orpc.communications.fax.numbers.queryOptions({ input: { facilityId } }));
  const faxes = useQuery({
    ...orpc.communications.fax.list.queryOptions({
      input: {
        facilityId,
        unfiledOnly: search.unfiled,
        search: search.q,
        faxNumberId: search.faxNumberId,
        dateFrom: search.dateFrom,
        dateTo: search.dateTo,
        direction: search.direction,
        status: search.status,
        sort: search.sortBy,
        dir: search.sortDir,
        limit: pageSize,
        offset: (page - 1) * pageSize,
      },
    }),
    placeholderData: keepWhileSame({ facilityId }),
  });

  // Everything about a fax's progress arrives by webhook, and a webhook that is
  // missed is missed forever: the row sits at "queued" and nobody learns whether
  // the pharmacy got the order. This is the way back.
  const sync = useMutation(
    orpc.communications.fax.sync.mutationOptions({
      onSuccess: async (result) => {
        toast.success(
          result.updated > 0
            ? `${result.updated} of ${result.checked} brought up to date`
            : `Nothing had changed on ${result.checked} still in flight`,
        );
        await invalidateKeys(queryClient, faxReaders());
      },
      onError: fail,
    }),
  );

  const retry = useMutation(
    orpc.communications.fax.retry.mutationOptions({
      onSuccess: async (fax) => {
        if (fax.status === "failed") toast.error(fax.errorMessage ?? "It failed again.");
        else toast.success("Sending again");
        await invalidateKeys(queryClient, faxReaders());
      },
      onError: fail,
    }),
  );

  const open = async (faxId: string) => {
    try {
      const { url } = await orpcClient.communications.fax.downloadUrl({ faxId });
      openLink(url);
    } catch (error) {
      fail(error instanceof Error ? error : new Error("Could not open the fax"));
    }
  };

  const sortProps = { sortBy: search.sortBy, sortDir: search.sortDir, onSort: applySort };

  // Every filter field is written, not merged: Clear hands back an empty set,
  // and a spread over the current search would quietly keep what it dropped.
  function applyFilters(next: FaxFilterValues) {
    navigate({
      search: {
        sortBy: search.sortBy,
        sortDir: search.sortDir,
        pageSize: search.pageSize,
        q: next.q,
        direction: next.direction,
        status: next.status,
        faxNumberId: next.faxNumberId,
        dateFrom: next.dateFrom,
        dateTo: next.dateTo,
        unfiled: next.unfiled,
        // A narrower question is asked from the first page of its answer.
        page: undefined,
      },
      // Typing in the search box rewrites the entry rather than adding one per pause.
      ...(next.q === search.q ? {} : { ...KEEP_PLACE }),
    });
  }
  function applySort(column: FaxSortKey, direction: "asc" | "desc") {
    navigate({ search: { ...search, sortBy: column, sortDir: direction } });
  }

  return (
    <>
      <PageHeader
        title="Fax & email"
        /* The building's own line, because "send us a fax" is a question staff
           are asked on the telephone and the number was on no screen. */
        meta={
          numbers.data && numbers.data.length > 0
            ? `${numbers.data.map((number) => number.faxNumber).join(", ")}, what the line received, and what still needs filing.`
            : "What the fax number received, and what still needs filing."
        }
        actions={
          canManage || canSend ? (
            <FaxHeaderActions
              facilityId={facilityId}
              canManage={canManage}
              canSend={canSend}
              failedCount={overview.data?.failed ?? 0}
              syncPending={sync.isPending}
              onSync={() => sync.mutate({ facilityId })}
              onEmail={() => setEmailing(true)}
              onCompose={() => setComposing(true)}
            />
          ) : undefined
        }
      />

      {overview.data ? (
        <StatTiles>
          {[
            { label: "Received", value: overview.data.inbound },
            { label: "Sent", value: overview.data.outbound },
            { label: "Needs filing", value: overview.data.unfiled },
            { label: "Failed", value: overview.data.failed },
          ].map((tile) => (
            <StatTile key={tile.label} label={tile.label} value={tile.value} />
          ))}
        </StatTiles>
      ) : null}

      <FaxFilters
        values={{
          q: search.q,
          direction: search.direction,
          status: search.status,
          faxNumberId: search.faxNumberId,
          dateFrom: search.dateFrom,
          dateTo: search.dateTo,
          unfiled: search.unfiled,
        }}
        numbers={numbers.data ?? []}
        onChange={applyFilters}
      />

      {faxes.isPending ? (
        <Skeleton className="h-64 w-full" />
      ) : faxes.isError ? (
        <ReadFailed
          message="Could not load the fax log."
          error={faxes.error}
          onRetry={() => void faxes.refetch()}
          retrying={faxes.isFetching}
        />
      ) : faxes.data && faxes.data.rows.length > 0 ? (
        <div
          aria-busy={faxes.isPlaceholderData || undefined}
          className={cn("transition-opacity", faxes.isPlaceholderData && "opacity-60")}
        >
          <FaxLogTable
            rows={faxes.data.rows}
            total={faxes.data.total}
            page={page}
            pageSize={pageSize}
            sortProps={sortProps}
            facilityId={facilityId}
            book={book}
            canManage={canManage}
            canSend={canSend}
            canFile={canFile}
            retryPending={retry.isPending}
            onShow={setShowing}
            onOpen={(faxId) => open(faxId)}
            onFileOnChart={setFiling}
            onFileNumber={setFilingNumber}
            onSendAgain={(faxId) => retry.mutate({ faxId })}
            onAssign={setAssigning}
            onPage={(next) =>
              navigate({ search: { ...search, page: next > 1 ? next : undefined } })
            }
            onPageSize={(size) =>
              navigate({ search: { ...search, pageSize: size, page: undefined } })
            }
          />
        </div>
      ) : faxes.isPlaceholderData ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <EmptyState title="Nothing here" description="Faxes the building receives appear here." />
      )}

      <FaxDetailDialog
        faxId={showing}
        canManage={canManage}
        retrying={retry.isPending}
        onClose={() => setShowing(null)}
        onOpenDocument={(faxId) => void open(faxId)}
        onRetry={(faxId) => retry.mutate({ faxId })}
      />

      <SendFaxDialog open={composing} onOpenChange={setComposing} facilityId={facilityId} />
      <SendDocumentsEmailDialog
        open={emailing}
        onOpenChange={setEmailing}
        facilityId={facilityId}
      />
      <AssignFacilityDialog
        key={assigning?.id ?? "none"}
        faxId={assigning?.id ?? null}
        currentFacilityId={assigning?.facilityId ?? null}
        onOpenChange={(next) => !next && setAssigning(null)}
      />
      <FileOnChartDialog
        faxId={filing}
        facilityId={facilityId}
        onOpenChange={(next) => !next && setFiling(null)}
      />
      <FileRecipientDialog
        open={filingNumber !== null}
        onOpenChange={(next) => !next && setFilingNumber(null)}
        channel="fax"
        value={filingNumber ?? ""}
        facilityId={facilityId === "all" ? null : facilityId}
      />
    </>
  );
}
